import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, Building2, CheckCircle2, RotateCcw, Inbox, Handshake } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const TABS = [["all", "All"], ["contact", "Contact"], ["partnership", "Partnership"], ["partner", "Partner Program"], ["payment_gateway", "Payment Gateways"]];

export default function AdminLeads() {
  const [subs, setSubs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);
  const [fetched, setFetched] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (filter !== "all") params.type = filter;
    const r = await api.get("/admin/contact", { params });
    setSubs(r.data.submissions || []);
    setFetched(true);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (s) => {
    const next = s.status === "new" ? "handled" : "new";
    setBusyId(s.id);
    try {
      await api.patch(`/admin/contact/${s.id}`, { status: next });
      setSubs((rows) => rows.map((r) => (r.id === s.id ? { ...r, status: next } : r)));
      toast.success(next === "handled" ? "Marked as handled" : "Reopened");
    } catch { toast.error("Update failed."); } finally { setBusyId(null); }
  };

  const fmt = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="mt-8" data-testid="admin-leads">
      <div className="flex items-center gap-2">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-sm text-sm border transition-colors ${filter === k ? "border-orange-300 bg-orange-50 text-orange-700" : "border-border bg-card text-muted-foreground hover:text-foreground"}`} data-testid={`leads-filter-${k}`}>{l}</button>
        ))}
      </div>

      <div className="mt-6 space-y-4">
        {fetched && subs.length === 0 && (
          <div className="py-14 text-center text-muted-foreground"><Inbox className="h-8 w-8 mx-auto opacity-50" /><p className="mt-3">No submissions yet.</p></div>
        )}
        {subs.map((s, i) => (
          <motion.div key={s.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.03 }}
            className="rounded-xl border border-border bg-card p-5 card-soft" data-testid={`lead-row-${s.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading text-lg text-foreground">{s.name}</h3>
                  {s.type === "partnership" && <Badge className="rounded-sm border border-orange-200 bg-orange-100 text-orange-700 hover:bg-orange-100 text-[10px] uppercase tracking-wider"><Handshake className="h-3 w-3 mr-1" /> Partner</Badge>}
                  {s.type === "partner" && <Badge className="rounded-sm border border-primary/30 bg-primary/10 text-primary hover:bg-primary/10 text-[10px] uppercase tracking-wider"><Handshake className="h-3 w-3 mr-1" /> Partner Program</Badge>}
                  {s.type === "payment_gateway" && <Badge className="rounded-sm border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-50 text-[10px] uppercase tracking-wider">Payment Gateway</Badge>}
                  <Badge className={`rounded-sm border text-[10px] uppercase tracking-wider ${s.status === "new" ? "border-blue-200 bg-blue-50 text-blue-700" : "border-green-200 bg-green-50 text-green-700"}`}>{s.status}</Badge>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <a href={`mailto:${s.email}`} className="flex items-center gap-1.5 hover:text-orange-700"><Mail className="h-3.5 w-3.5" /> {s.email}</a>
                  {s.phone && <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {s.phone}</span>}
                  {s.company && <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> {s.company}</span>}
                </div>
                {s.service && <p className="mt-2 text-xs"><span className="text-muted-foreground">Interest:</span> <span className="text-orange-700">{s.service}</span></p>}
                {s.type === "partner" && (
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    Partner type: <span className="text-foreground">{s.partner_type || "—"}</span> · Country: <span className="text-foreground">{s.country || "—"}</span>
                    {s.website && <> · <a href={s.website} target="_blank" rel="noreferrer" className="text-orange-700 hover:underline">{s.website}</a></>}
                    {s.services?.length > 0 && <> · Services: <span className="text-foreground">{s.services.join(", ")}</span></>}
                  </p>
                )}
                {s.type === "payment_gateway" && s.assessment && (
                  <dl className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs" data-testid={`lead-assessment-${s.id}`}>
                    {[["Jurisdiction", s.assessment.registration_jurisdiction],
                      ["Business model", s.assessment.business_model],
                      ["Support required", (s.assessment.support_required || []).join(", ")],
                      ["Activity", s.assessment.business_activity],
                      ["Website / app", s.assessment.website_not_live ? "Not live yet" : s.assessment.website_url],
                      ["Target markets", s.assessment.target_markets],
                      ["Currencies", s.assessment.currencies],
                      ["Monthly volume", s.assessment.monthly_volume ? s.assessment.monthly_volume + (s.assessment.monthly_volume !== "Not sure" ? " " + (s.assessment.volume_currency || "") : "") : ""],
                      ["Platform", s.assessment.platform],
                      ["Existing provider", s.assessment.existing_provider],
                      ["Capabilities", (s.assessment.capabilities || []).join(", ")],
                    ].filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} className="flex gap-2"><dt className="text-muted-foreground w-28 shrink-0">{k}</dt><dd className="text-foreground break-words">{v}</dd></div>
                    ))}
                  </dl>
                )}
                <p className="mt-2 text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">{s.message}</p>
                <p className="mt-2 text-[11px] text-muted-foreground font-mono">{fmt(s.created_at)}</p>
              </div>
              <Button size="sm" variant="outline" disabled={busyId === s.id} onClick={() => toggle(s)} className={`rounded-sm h-8 text-xs shrink-0 ${s.status === "new" ? "border-green-300 text-green-700 hover:bg-green-50" : "border-slate-300 text-muted-foreground hover:bg-secondary"}`} data-testid={`lead-toggle-${s.id}`}>
                {s.status === "new" ? <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark handled</> : <><RotateCcw className="h-3.5 w-3.5 mr-1" /> Reopen</>}
              </Button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
